import { useMemo } from "react";

import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";
import InputText from "commons/ui/input/InputText";
import LineSeparator from "commons/ui/LineSeparator";

import ExtensionsProporsionInfo from "components/subComponents/ExtensionsProporsionInfo";
import TwoLanguagesAdornment from "components/subComponents/TwoLanguagesAdornment";
import AttachmentPicker from "components/ui/AttachmentPicker";
import TitleFunctionsTopBlock from "components/ui/TitleFunctionsTopBlock";

import useCheckSectionErrors from "hooks/form/blocks/useCheckSectionErrors";
import useSectionOneImageOneText from "hooks/form/blocks/useSectionOneImageOneText";
import useTitleFunctionsTopBlock from "hooks/useTitleFunctionsTopBlock";

import { BlockEnum, DesktopOrMobile } from "types/enums";

function SectionOneImageOneTextForm({ blockId }: { blockId: BlockEnum }) {
    const { buttonDelete, setMainImage, formHook } = useSectionOneImageOneText();

    useCheckSectionErrors({ formHook });

    const { checkErrorVisibility, form, setValue } = formHook;

    const titleFunctionsTopBlockHook = useTitleFunctionsTopBlock({
        buttonDelete: buttonDelete,
    });

    const dataBlock = useMemo(() => {
        const finalData = {
            title: "Titolo",
            input_label: "Titolo",
            isVideo: false,
            isText: false,
        };

        if (
            blockId === BlockEnum.SectionVideoSideLeft ||
            blockId === BlockEnum.SectionVideoSideRight
        ) {
            finalData.title = "Video";
            finalData.input_label = "Url del video";
            finalData.isVideo = true;
        } else if (
            blockId === BlockEnum.SectionOneImageRightOneTexts ||
            blockId === BlockEnum.SectionOneImageLeftOneTexts
        ) {
            finalData.title = "Testi";
            finalData.input_label = "Testo";
            finalData.isText = true;
        }

        return finalData;
    }, [blockId]);

    return (
        <>
            <TitleFunctionsTopBlock titleFunctionsTopBlockHook={titleFunctionsTopBlockHook} />

            {titleFunctionsTopBlockHook.closedBlock && (
                <>
                    <GridColumn>
                        {dataBlock.isVideo && (
                            <ExtensionsProporsionInfo
                                proportion={"quadrata o verticale"}
                                desktopText={"1000x1500px (max 400KB)"}
                                mobileText={"667x1000px (max 250KB)"}
                            />
                        )}

                        <GridRow>
                            <GridItem>
                                <h3>{dataBlock.isVideo ? "Immagine laterale" : "Immagine"}</h3>
                            </GridItem>
                        </GridRow>

                        <GridRow>
                            <GridItem className={"col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"Foto Desktop"}
                                    setFile={(file) => setMainImage(file, DesktopOrMobile.desktop)}
                                    file={form.img.desktop}
                                    accept={".png,.jpg,.jpeg"}
                                    errors={checkErrorVisibility("img.desktop")}
                                />
                            </GridItem>

                            <GridItem className={"col-xs-24-12"}>
                                <AttachmentPicker
                                    buttonLabel={"Foto Mobile"}
                                    setFile={(file) => setMainImage(file, DesktopOrMobile.mobile)}
                                    file={form.img.mobile}
                                    accept={".png,.jpg,.jpeg"}
                                    errors={checkErrorVisibility("img.mobile")}
                                />
                            </GridItem>
                        </GridRow>
                    </GridColumn>

                    <LineSeparator />

                    <GridColumn>
                        <GridRow>
                            <GridItem>
                                <h3>{dataBlock.title}</h3>
                            </GridItem>
                        </GridRow>

                        <GridItem>
                            <GridRow>
                                <div className={"col-xs-24-12"}>
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={dataBlock.input_label}
                                                startAdornment={TwoLanguagesAdornment("it")}
                                                value={form.text.it.text}
                                                setValue={setValue}
                                                path={"text.it.text"}
                                                checkErrorVisibility={checkErrorVisibility}
                                                autoFocus
                                                textarea={dataBlock.isText}
                                                largeBox={dataBlock.isText}
                                            />
                                        </GridItem>
                                    </GridRow>
                                </div>

                                <div className={"col-xs-24-12"}>
                                    <GridRow>
                                        <GridItem className={"col-xs-12-12"}>
                                            <InputText
                                                label={dataBlock.input_label}
                                                startAdornment={TwoLanguagesAdornment("en")}
                                                value={form.text.en.text}
                                                setValue={setValue}
                                                path={"text.en.text"}
                                                checkErrorVisibility={checkErrorVisibility}
                                                textarea={dataBlock.isText}
                                                largeBox={dataBlock.isText}
                                            />
                                        </GridItem>
                                    </GridRow>
                                </div>
                            </GridRow>
                        </GridItem>
                    </GridColumn>
                </>
            )}
        </>
    );
}

export default SectionOneImageOneTextForm;
